import { useEffect, useState } from "react";
import { g, eur, api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const oggi = () => new Date().toISOString().slice(0, 10);

export default function Movimenti() {
  const [rows, setRows] = useState([]);
  const [by, setBy] = useState("");
  const [action, setAction] = useState("");
  const [date, setDate] = useState(oggi());

  useEffect(() => {
    g("/movements", { by, action, date }).then(setRows);
  }, [by, action, date]);

  const totale = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const perAzione = Object.values(
    rows.reduce((acc, r) => {
      const k = r.action || "—";
      acc[k] = acc[k] || { action: k, n: 0, amount: 0 };
      acc[k].n += 1;
      acc[k].amount += r.amount || 0;
      return acc;
    }, {})
  ).sort((a, b) => b.n - a.n);

  const scarica = async () => {
    const r = await api.get("/movements/export.csv", { params: { by, action, date }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `movimenti-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fade-up space-y-5" data-testid="movimenti-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Pannello titolare</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Movimenti</h1>
          <p className="text-xs text-slate-500">Ogni azione della serata: chi, quando e quanto.</p>
        </div>
        <Button variant="outline" onClick={scarica} data-testid="mov-export-btn">
          <Download className="mr-1.5 h-4 w-4" /> Scarica CSV
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input type="date" className="h-10 w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mov-date-input" />
        <Input className="h-10 w-48" placeholder="Filtra per persona" value={by} onChange={(e) => setBy(e.target.value)} data-testid="mov-by-input" />
        <Input className="h-10 w-48" placeholder="Filtra per azione" value={action} onChange={(e) => setAction(e.target.value)} data-testid="mov-action-input" />
        {(by || action || date !== oggi()) && (
          <Button
            variant="ghost"
            className="h-10"
            data-testid="mov-reset-btn"
            onClick={() => {
              setBy("");
              setAction("");
              setDate(oggi());
            }}
          >
            Azzera i filtri
          </Button>
        )}
      </div>

      <Card className="p-5" data-testid="mov-totals">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <span className="text-sm text-slate-500">
            Movimenti: <b className="font-num text-slate-900">{rows.length}</b>
          </span>
          <span className="text-sm text-slate-500">
            Totale importi: <b className="font-num text-slate-900">{eur(totale)}</b>
          </span>
        </div>
        {perAzione.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {perAzione.slice(0, 10).map((a) => (
              <button
                key={a.action}
                onClick={() => setAction(a.action)}
                data-testid={`mov-tag-${a.action.replace(/\s+/g, "-").toLowerCase()}`}
                className="font-num rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 transition-colors hover:border-slate-900 hover:text-slate-900"
              >
                {a.action} · {a.n}
                {a.amount ? ` · ${eur(a.amount)}` : ""}
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5" data-testid="movimenti-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <th className="py-2 pr-3 font-bold">Ora</th>
                <th className="py-2 pr-3 font-bold">Azione</th>
                <th className="py-2 pr-3 font-bold">Chi</th>
                <th className="py-2 pr-3 font-bold">Tavolo</th>
                <th className="py-2 pr-3 font-bold">Dettaglio</th>
                <th className="py-2 text-right font-bold">Importo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`mov-${r.id}`}>
                  <td className="py-2.5 pr-3 font-num text-slate-500">
                    {new Date(r.at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="py-2.5 pr-3 font-medium">{r.action}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{r.by}</td>
                  <td className="py-2.5 pr-3 font-num">{r.table || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-500">{r.detail}</td>
                  <td className="py-2.5 text-right font-num">{r.amount ? eur(r.amount) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <p className="py-6 text-sm text-slate-500">Nessun movimento con questi filtri.</p>}
        </div>
      </Card>
    </div>
  );
}
